import Link from "next/link"
import { NewsletterCalloutForm } from "./newsletter-callout-form"

interface DarkCalloutBaseProps {
  /**
   * Background variant.
   * - `dark` (default): gray-900 background with orange glow (top-right).
   *   Used for "Free tool" / utility cross-sells.
   * - `blue`: brand blue (#0469f0) with orange glow (bottom-left).
   *   Used for "Newsletter" / subscribe CTAs.
   */
  variant?: "dark" | "blue"
  /** Small uppercase eyebrow label (e.g. "Free tool", "Newsletter"). */
  eyebrow: string
  /**
   * Main headline. ReactNode so callers can drop italic emphasis spans
   * (e.g. `<em>your</em>`) inline.
   */
  headline: React.ReactNode
  /** Body copy under the headline. */
  body: string
  /** CTA button label. */
  cta: string
  /**
   * Headline font size in px (default 19). FRIDGE-1188: the article-sidebar
   * newsletter headline must sit on one line in the 276px text box, which
   * needs 18px.
   */
  headlineSize?: number
}

/**
 * The CTA is EITHER a link (`href`) OR a real newsletter signup
 * (`subscribeSource`, FRIDGE-357). A "Subscribe" button must capture the
 * address — it posts to /api/alerts/subscribe and `subscribeSource` becomes
 * the row's `source` in `newsletter_subscriptions`. Never point a subscribe
 * CTA at /contact: that captured nothing.
 */
type DarkCalloutProps = DarkCalloutBaseProps &
  ({ href: string; subscribeSource?: undefined } | { subscribeSource: string; href?: undefined })

/**
 * DarkCallout — sidebar callout card with a dark background, orange glow
 * blur, small uppercase eyebrow, bold headline, body copy, and a CTA button.
 *
 * Two variants ship today: `dark` (gray-900) for utility cross-sells like
 * "Free tool" calculators, and `blue` (brand blue) for "Newsletter" / list
 * subscribe CTAs. Both use an absolutely-positioned blurred orange glow as
 * the brand accent — top-right on dark, bottom-left on blue.
 *
 * Lifted from the editorial mockups (blog-post + blogs-news), where the same
 * pattern was inlined three times. Now graduated for reuse on any editorial
 * surface that needs a high-contrast inline callout in a content stream.
 */
export function DarkCallout({
  variant = "dark",
  eyebrow,
  headline,
  body,
  href,
  subscribeSource,
  cta,
  headlineSize = 19,
}: DarkCalloutProps) {
  const isBlue = variant === "blue"
  const cardBg = isBlue ? "#0469f0" : "var(--gray-900)"
  const glowPos = isBlue
    ? { bottom: -40, left: -30, width: 180, height: 180, blur: 60, alpha: 0.3 }
    : { top: -30, right: -30, width: 160, height: 160, blur: 50, alpha: 0.35 }
  const bodyColor = isBlue ? "rgba(255,255,255,0.85)" : "#d1d5db"
  const buttonBg = isBlue ? "#fff" : "var(--fridge-orange)"
  const buttonColor = isBlue ? "var(--fridge-blue)" : "#fff"
  const buttonWeight = isBlue ? 800 : 700

  return (
    <div
      style={{
        background: cardBg,
        color: "#fff",
        borderRadius: 12,
        padding: 22,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          ...(glowPos.top !== undefined && { top: glowPos.top }),
          ...(glowPos.right !== undefined && { right: glowPos.right }),
          ...(glowPos.bottom !== undefined && { bottom: glowPos.bottom }),
          ...(glowPos.left !== undefined && { left: glowPos.left }),
          width: glowPos.width,
          height: glowPos.height,
          background: `rgba(255,153,81,${glowPos.alpha})`,
          filter: `blur(${glowPos.blur}px)`,
          borderRadius: "50%",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "var(--fridge-orange)",
          marginBottom: 10,
          position: "relative",
        }}
      >
        {eyebrow}
      </div>
      <div
        style={{
          fontSize: headlineSize,
          fontWeight: 900,
          letterSpacing: "-0.02em",
          lineHeight: 1.2,
          marginBottom: 10,
          position: "relative",
        }}
      >
        {headline}
      </div>
      <p
        style={{
          fontSize: 13,
          color: bodyColor,
          lineHeight: 1.5,
          margin: "0 0 14px",
          position: "relative",
        }}
      >
        {body}
      </p>
      {subscribeSource !== undefined ? (
        <NewsletterCalloutForm
          source={subscribeSource}
          cta={cta}
          buttonBg={buttonBg}
          buttonColor={buttonColor}
          buttonWeight={buttonWeight}
        />
      ) : (
        <Link
          href={href}
          style={{
            display: "block",
            background: buttonBg,
            color: buttonColor,
            padding: "11px 14px",
            borderRadius: 8,
            fontSize: 13,
            fontWeight: buttonWeight,
            textDecoration: "none",
            textAlign: "center",
            position: "relative",
          }}
        >
          {cta}
        </Link>
      )}
    </div>
  )
}
